import { useTranslation } from "react-i18next";
import { Alert } from "@/ui/alert/Alert";
import { Btn } from "@/ui/btn/Btn";
import { Dropdown } from "@/ui/dropdown/Dropdown";
import { Menu } from "@/ui/menu/Menu";
import { useMedia } from "@/hooks/useMedia";
import { gtmEvent } from "@/utils/google";
import { Network } from "@/utils/network";
import {
  useInstagramSearch,
  useInstagramSearchInfluencersFilters,
  useInstagramSearchProfilesFilters,
} from "../../hooks/useInstagramSearch";
import { useIsInstagramSearchInfluencers } from "../../hooks/useInstagramSearchRouter";
import type { InstagramSearch } from "../../types";
import { requestInstagramSearchAuth } from "../../utils/utils";
import styles from "./InstagramSearchSelectionDownload.module.css";

interface InstagramSearchSelectionDownloadProps {
  profiles: Set<InstagramSearch.Profile["id"]>;
}

export const InstagramSearchSelectionDownload: React.FC<
  InstagramSearchSelectionDownloadProps
> = ({ profiles }) => {
  const { t } = useTranslation();
  const config = useInstagramSearch((state) => state.config);
  const isAuthorized = useInstagramSearch((state) => state.isAuthorized);
  const isSubscribed = useInstagramSearch((state) => state.isSubscribed);
  const influencersFilters = useInstagramSearchInfluencersFilters();
  const profilesFilters = useInstagramSearchProfilesFilters();
  const isInfluencers = useIsInstagramSearchInfluencers();
  const sm = useMedia("<sm");

  const filters =
    profiles.size === 0
      ? isInfluencers
        ? influencersFilters
        : profilesFilters
      : {
          profiles: Array.from(profiles),
        };

  const csv = Network.url(config.api.exportCsv, filters);
  const xls = Network.url(config.api.exportXls, filters);

  return (
    <Dropdown>
      <Dropdown.Toggle>
        <Dropdown.Context.Consumer>
          {(dropdown) => (
            <Btn
              icon="download_24px_regular"
              text={
                profiles.size === 0
                  ? t("selection.downloadAll")
                  : sm === false
                    ? t("selection.download")
                    : undefined
              }
              active={dropdown.open}
              color="grey"
              size="sm"
              data-event-section="search-export"
              data-event-source="download-checked"
            />
          )}
        </Dropdown.Context.Consumer>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Context.Consumer>
          {(dropdown) => (
            <Menu className={styles.menu}>
              {isAuthorized === false && (
                <>
                  <Menu.Group>
                    <Alert
                      className={styles.alert}
                      text={t("selection.authorizeToDownload")}
                      appearance="orange"
                      size="sm"
                    />
                  </Menu.Group>
                  <Menu.Group>
                    <Btn
                      icon="confirm_24px_filled"
                      text={t("selection.signUp")}
                      color="primary"
                      onClick={() => {
                        dropdown.close();
                        requestInstagramSearchAuth();
                      }}
                    />
                  </Menu.Group>
                </>
              )}
              {isAuthorized === true && isSubscribed === false && (
                <>
                  <Menu.Group>
                    <Alert
                      className={styles.alert}
                      text={t("selection.subscribeToDownload")}
                      appearance="orange"
                      size="sm"
                    />
                  </Menu.Group>
                  <Menu.Group>
                    <Btn
                      href={config.links.instagramSearchSubscribe}
                      icon="confirm_24px_filled"
                      text={t("selection.subscribe")}
                      color="primary"
                    />
                  </Menu.Group>
                </>
              )}
              {isAuthorized === true && isSubscribed === true && (
                <Menu.Group>
                  <a href={xls.toString()} target="_blank" rel="nofollow">
                    <Menu.Item
                      onClick={() => {
                        dropdown.close();
                        gtmEvent(
                          "search",
                          "search_click_download_checked",
                          "submit",
                        );
                      }}
                    >
                      <Menu.Text>{t("selection.downloadXLS")}</Menu.Text>
                    </Menu.Item>
                  </a>
                  <a href={csv.toString()} target="_blank" rel="nofollow">
                    <Menu.Item
                      onClick={() => {
                        dropdown.close();
                        gtmEvent(
                          "search",
                          "search_click_download_checked",
                          "submit",
                        );
                      }}
                    >
                      <Menu.Text>{t("selection.downloadCSV")}</Menu.Text>
                    </Menu.Item>
                  </a>
                </Menu.Group>
              )}
            </Menu>
          )}
        </Dropdown.Context.Consumer>
      </Dropdown.Menu>
    </Dropdown>
  );
};

interface InstagramSearchListsSelectionDownloadProps {
  lists: InstagramSearch.List[];
}

export const InstagramSearchListsSelectionDownload: React.FC<
  InstagramSearchListsSelectionDownloadProps
> = ({ lists }) => {
  const { t } = useTranslation();
  const config = useInstagramSearch((state) => state.config);
  const isAuthorized = useInstagramSearch((state) => state.isAuthorized);
  const isSubscribed = useInstagramSearch((state) => state.isSubscribed);
  const sm = useMedia("<sm");

  const data = {
    lists: lists.map((list) => list.id),
  };

  const csv = Network.url(config.api.exportCsv, data);
  const xls = Network.url(config.api.exportXls, data);

  return (
    <Dropdown>
      <Dropdown.Toggle>
        <Dropdown.Context.Consumer>
          {(dropdown) => (
            <Btn
              icon="download_24px_regular"
              text={
                lists.length === 0
                  ? t("selection.downloadAll")
                  : sm === false
                    ? t("selection.download")
                    : undefined
              }
              active={dropdown.open}
              color="grey"
              size="sm"
              data-event-section="search-export"
              data-event-source="download-checked"
            />
          )}
        </Dropdown.Context.Consumer>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Context.Consumer>
          {(dropdown) => (
            <Menu className={styles.menu}>
              {isAuthorized === false && (
                <>
                  <Menu.Group>
                    <Alert
                      className={styles.alert}
                      text={t("selection.authorizeToDownload")}
                      appearance="orange"
                      size="sm"
                    />
                  </Menu.Group>
                  <Menu.Group>
                    <Btn
                      icon="confirm_24px_filled"
                      text={t("selection.signUp")}
                      color="primary"
                      onClick={() => {
                        dropdown.close();
                        requestInstagramSearchAuth();
                      }}
                    />
                  </Menu.Group>
                </>
              )}
              {isAuthorized === true && isSubscribed === false && (
                <>
                  <Menu.Group>
                    <Alert
                      className={styles.alert}
                      text={t("selection.subscribeToDownload")}
                      appearance="orange"
                      size="sm"
                    />
                  </Menu.Group>
                  <Menu.Group>
                    <Btn
                      href={config.links.instagramSearchSubscribe}
                      icon="confirm_24px_filled"
                      text={t("selection.subscribe")}
                      color="primary"
                    />
                  </Menu.Group>
                </>
              )}
              {isAuthorized === true && isSubscribed === true && (
                <Menu.Group>
                  <a href={xls.toString()} target="_blank" rel="nofollow">
                    <Menu.Item
                      onClick={() => {
                        dropdown.close();
                        gtmEvent(
                          "search",
                          "search_click_download_checked",
                          "submit",
                        );
                      }}
                    >
                      <Menu.Text>{t("selection.downloadXLS")}</Menu.Text>
                    </Menu.Item>
                  </a>
                  <a href={csv.toString()} target="_blank" rel="nofollow">
                    <Menu.Item
                      onClick={() => {
                        dropdown.close();
                        gtmEvent(
                          "search",
                          "search_click_download_checked",
                          "submit",
                        );
                      }}
                    >
                      <Menu.Text>{t("selection.downloadCSV")}</Menu.Text>
                    </Menu.Item>
                  </a>
                </Menu.Group>
              )}
            </Menu>
          )}
        </Dropdown.Context.Consumer>
      </Dropdown.Menu>
    </Dropdown>
  );
};

interface InstagramSearchListDownloadProps {
  listId: InstagramSearch.List["id"];
  profiles: Set<InstagramSearch.Profile["id"]>;
}

export const InstagramSearchListDownload: React.FC<
  InstagramSearchListDownloadProps
> = ({ listId, profiles }) => {
  const { t } = useTranslation();
  const config = useInstagramSearch((state) => state.config);
  const isAuthorized = useInstagramSearch((state) => state.isAuthorized);
  const isSubscribed = useInstagramSearch((state) => state.isSubscribed);
  const sm = useMedia("<sm");

  const filters =
    profiles.size === 0
      ? {
          lists: [listId],
        }
      : {
          profiles: Array.from(profiles),
        };

  const csv = Network.url(config.api.exportCsv, filters);
  const xls = Network.url(config.api.exportXls, filters);

  return (
    <Dropdown>
      <Dropdown.Toggle>
        <Dropdown.Context.Consumer>
          {(dropdown) => (
            <Btn
              icon="download_24px_regular"
              text={
                profiles.size === 0
                  ? t("selection.downloadAll")
                  : sm === false
                    ? t("selection.download")
                    : undefined
              }
              active={dropdown.open}
              color="grey"
              size="sm"
              data-event-section="search-export"
              data-event-source="download-checked"
            />
          )}
        </Dropdown.Context.Consumer>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Context.Consumer>
          {(dropdown) => (
            <Menu className={styles.menu}>
              {isAuthorized === false && (
                <>
                  <Menu.Group>
                    <Alert
                      className={styles.alert}
                      text={t("selection.authorizeToDownload")}
                      appearance="orange"
                      size="sm"
                    />
                  </Menu.Group>
                  <Menu.Group>
                    <Btn
                      icon="confirm_24px_filled"
                      text={t("selection.signUp")}
                      color="primary"
                      onClick={() => {
                        dropdown.close();
                        requestInstagramSearchAuth();
                      }}
                    />
                  </Menu.Group>
                </>
              )}
              {isAuthorized === true && isSubscribed === false && (
                <>
                  <Menu.Group>
                    <Alert
                      className={styles.alert}
                      text={t("selection.subscribeToDownload")}
                      appearance="orange"
                      size="sm"
                    />
                  </Menu.Group>
                  <Menu.Group>
                    <Btn
                      href={config.links.instagramSearchSubscribe}
                      icon="confirm_24px_filled"
                      text={t("selection.subscribe")}
                      color="primary"
                    />
                  </Menu.Group>
                </>
              )}
              {isAuthorized === true && isSubscribed === true && (
                <Menu.Group>
                  <a href={xls.toString()} target="_blank" rel="nofollow">
                    <Menu.Item
                      onClick={() => {
                        dropdown.close();
                        gtmEvent(
                          "search",
                          "search_click_download_checked",
                          "submit",
                        );
                      }}
                    >
                      <Menu.Text>{t("selection.downloadXLS")}</Menu.Text>
                    </Menu.Item>
                  </a>
                  <a href={csv.toString()} target="_blank" rel="nofollow">
                    <Menu.Item
                      onClick={() => {
                        dropdown.close();
                        gtmEvent(
                          "search",
                          "search_click_download_checked",
                          "submit",
                        );
                      }}
                    >
                      <Menu.Text>{t("selection.downloadCSV")}</Menu.Text>
                    </Menu.Item>
                  </a>
                </Menu.Group>
              )}
            </Menu>
          )}
        </Dropdown.Context.Consumer>
      </Dropdown.Menu>
    </Dropdown>
  );
};
